/**
 * En qué estado se dibuja un panel del folio.
 *
 * Vive acá y no adentro de cada grilla porque las dos —el editor (`PanelsGrid`) y el portal
 * (`StaticPanelsGrid`)— tenían el mismo ternario copiado, y con una diferencia que nadie quería:
 * el editor conocía el estado «calculando» y el portal no.
 *
 * El motivo real de este módulo (card `4By6KR9g`): **el estado vacío era el default de todo lo que
 * no se hubiera clasificado**. Cuando el informe entero fallaba, el hook dejaba `renderResult` en
 * `null`, la grilla calculaba un estado `null`, igual renderizaba el cuerpo del panel con
 * `data={null}`, y cada cuerpo terminaba en su propio `return <PanelState state="EMPTY"/>`. O sea:
 * la pantalla decía «Sin datos para este período» —una afirmación sobre el negocio del cliente—
 * cuando lo único cierto era que la consulta había fallado, con la franja roja del error a 100px
 * de ahí.
 *
 * `UNAVAILABLE` es ese caso, con nombre: no sabemos, y no lo disfrazamos de dato.
 */

import {PANEL_TYPES} from './informeModel';

export const PANEL_STATE = {
    LOADING: 'LOADING',
    OK: 'OK',
    EMPTY: 'EMPTY',
    ERROR: 'ERROR',
    NO_METRIC: 'NO_METRIC',
    UNAVAILABLE: 'UNAVAILABLE',
};

/**
 * ¿El aviso consolidado de arriba ya explica por qué falló ESTE panel?
 *
 * Review R-reports-fe-03: el aviso se SUMABA a la repetición en vez de reemplazarla. Con una cuenta
 * desconectada tumbando las doce consultas, cada panel entraba por `ERROR` con «Dashbo perdió el
 * acceso a una de las cuentas de este cliente.» —doce veces, en rojo— **más** el banner diciendo lo
 * mismo. El comentario de `PanelBodies` («el motivo y la acción se cuentan UNA vez, en la franja de
 * arriba») describía una intención que el código sólo cumplía en `UNAVAILABLE`, que es justo el
 * camino donde el banner no se dibuja: los dos caminos eran disjuntos.
 */
export function isExplainedByNotice(rendered, noticeCode) {
    return !!noticeCode && !!rendered && rendered.state === PANEL_STATE.ERROR
        && rendered.error_code === noticeCode;
}

/** Una tarjeta de métrica destacada a la que todavía no le eligieron qué medir. */
function isKpiWithoutMetric(panel) {
    return !!panel && panel.type === PANEL_TYPES.KPI && !((panel.metrics || []).length);
}

/**
 * @param rendered el panel que devolvió el motor (`{id, state, error_code, data}`), o `null` si el
 *        resultado no lo trae.
 * @param loading si hay un render en vuelo.
 * @param hasResult si el motor ya contestó algo (aunque a este panel no le toque nada).
 * @param reportFailed si el informe ENTERO falló. Manda sobre todo lo demás: con el render caído,
 *        ningún panel tiene datos y ninguno puede afirmar que no los haya.
 * @param noticeCode el código que el aviso consolidado ya explica arriba, si es que se dibuja. Los
 *        paneles que fallaron por ESE código bajan a `UNAVAILABLE` (gris, dos palabras); el rojo
 *        queda para los códigos que el aviso NO cubre, que son los que todavía tienen algo que
 *        contar. Se deriva del mismo `renderResult` que alimenta al aviso, a propósito: pasarlo
 *        por prop deja que un llamador se lo olvide y vuelvan las doce cajas.
 */
export function panelRenderState(rendered, {loading = false, hasResult = false, reportFailed = false,
                                            noticeCode = null, panel = null} = {}) {
    // Una tarjeta de métrica destacada SIN métrica elegida no está «sin datos para este período»:
    // no hay nada que consultar. Lo producía `KpiBody` desde adentro del cuerpo, y desde que el
    // cuerpo se dibuja sólo con `OK` ese `return` dejó de ser alcanzable — el enum prometía un
    // estado sin productor y la tarjeta vacía caía en el texto de «sin datos» (review
    // R-reports-fe-11). Va antes que `rendered` porque un panel sin métricas tampoco tiene fila en
    // la respuesta del motor.
    if (isKpiWithoutMetric(panel)) return PANEL_STATE.NO_METRIC;
    // Un bloque de texto no pide datos: siempre se dibuja, aunque el motor esté calculando o el
    // informe entero haya fallado. Su contenido sale del layout, no de la respuesta del motor.
    if (panel && panel.type === PANEL_TYPES.TEXT) return PANEL_STATE.OK;
    if (reportFailed) return PANEL_STATE.UNAVAILABLE;
    if (isExplainedByNotice(rendered, noticeCode)) return PANEL_STATE.UNAVAILABLE;
    if (rendered) return rendered.state;
    // Un panel sin datos mientras el render está en vuelo está CALCULANDO, no vacío: uno recién
    // agregado decía «Sin datos para este período» hasta que llegaba la respuesta (QA UI-09).
    if (loading) return PANEL_STATE.LOADING;
    // El motor contestó y este panel no vino en la respuesta: ahí sí, no hay nada que mostrar.
    if (hasResult) return PANEL_STATE.EMPTY;
    return PANEL_STATE.UNAVAILABLE;
}
